import { useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';

// ─── NAV DATA (shared by desktop sidebar + mobile drawer) ───────────────────
const navItems = [
  { section: "OVERVIEW",  items: [{ icon: "ti-layout-dashboard", label: "Dashboard",     path: "/dashboard"     }] },
  { section: "ENGAGE",    items: [
    { icon: "ti-robot",        label: "Nigella VOX",   path: "/ai-agent"      },
    { icon: "ti-message-dots", label: "Conversations", path: "/conversations" },
    { icon: "ti-users",        label: "Leads",         path: "/leads"         },
  ]},
  { section: "CONFIGURE", items: [
    { icon: "ti-book",     label: "Knowledge Base", path: "/knowledge" },
    { icon: "ti-plug",     label: "Channels",       path: "/channels"  },
    { icon: "ti-settings", label: "Settings",       path: "/settings"  },
  ]},
];

// ─── DESKTOP SIDEBAR ─────────────────────────────────────────────────────────
function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  
  return (
    <aside className="hidden lg:flex flex-col h-full border-r flex-shrink-0"
      style={{ width: "15%", minWidth: 180, background: "#161412", borderColor: "#2a2825" }}>
      <div className="flex items-center gap-3 px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
        <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <i className="ti ti-bolt text-white text-base" />
        </div>
        <div>
          <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
          <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
        </div>
      </div>
      <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
        {navItems.map(({ section, items }) => (
          <div key={section}>
            <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
            {items.map(({ icon, label, path }) => {
              const active = location.pathname === path;
              return (
                <div key={label}
                  onClick={() => navigate(path)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer mb-0.5 transition-all ${
                    active ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                           : "text-stone-400 hover:text-stone-200 hover:bg-white/5"}`}>
                  <i className={`ti ${icon} text-base flex-shrink-0`} />
                  <span className="text-xs font-medium">{label}</span>
                </div>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="px-5 pb-5 border-t pt-4" style={{ borderColor: "#2a2825" }}>
        <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
          <i className="ti ti-shield-check text-orange-700" /> Secured workspace
        </div>
        <div className="text-[9px] text-stone-700">© 2026 AWK Technologies</div>
      </div>
    </aside>
  );
}

// ─── MOBILE DRAWER MENU ──────────────────────────────────────────────────────
function MobileDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-40 lg:hidden" style={{ background: "rgba(0,0,0,0.7)" }} onClick={onClose} />
      <div className="fixed top-0 left-0 bottom-0 z-50 flex flex-col lg:hidden"
        style={{ width: 260, background: "#161412", borderRight: "1px solid #2a2825" }}>
        <div className="flex items-center justify-between px-5 py-5 border-b" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ti ti-bolt text-white text-base" />
            </div>
            <div>
              <div className="text-sm font-bold text-stone-100 leading-tight">Nigella VOX</div>
              <div className="text-[9px] text-stone-500 uppercase tracking-widest">Agent Console</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl flex items-center justify-center text-stone-500" style={{ background: "#252320" }}>
            <i className="ti ti-x text-sm" />
          </button>
        </div>
        <nav className="flex flex-col gap-5 px-3 py-5 flex-1 overflow-y-auto">
          {navItems.map(({ section, items }) => (
            <div key={section}>
              <div className="text-[9px] text-stone-600 uppercase tracking-widest mb-2 px-2">{section}</div>
              {items.map(({ icon, label, path }) => {
                const active = location.pathname === path;
                return (
                  <div key={label}
                    onClick={() => { navigate(path); onClose(); }}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer mb-0.5 transition-all ${
                      active ? "bg-orange-600/20 text-orange-400 border border-orange-600/30"
                             : "text-stone-400 hover:text-stone-200 hover:bg-white/5"}`}>
                    <i className={`ti ${icon} text-base flex-shrink-0`} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="px-5 pb-6 border-t pt-4" style={{ borderColor: "#2a2825" }}>
          <div className="flex items-center gap-1.5 text-[9px] text-stone-600 mb-1">
            <i className="ti ti-shield-check text-orange-700" /> Secured workspace
          </div>
          <div className="text-[9px] text-stone-700">© 2026 AWK Technologies</div>
        </div>
      </div>
    </>
  );
}

// ─── MOBILE BOTTOM NAV ───────────────────────────────────────────────────────
function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const items = [
    { icon: "ti-layout-dashboard", label: "Home",  path: "/dashboard"     },
    { icon: "ti-robot",            label: "VOX",   path: "/ai-agent"      },
    { icon: "ti-message-dots",     label: "Chats", path: "/conversations" },
    { icon: "ti-users",            label: "Leads", path: "/leads"         },
    { icon: "ti-book",             label: "KB",    path: "/knowledge"     },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-around border-t lg:hidden"
      style={{ background: "#161412", borderColor: "#2a2825", paddingBottom: "env(safe-area-inset-bottom)" }}>
      {items.map(({ icon, label, path }) => {
        const active = location.pathname === path;
        return (
          <button key={path} onClick={() => navigate(path)}
            className="flex flex-col items-center gap-0.5 py-2.5 px-3 flex-1 transition-all"
            style={{ color: active ? "#e8602a" : "#6b6560" }}>
            <i className={`ti ${icon} text-lg`} />
            <span style={{ fontSize: 9, fontWeight: active ? 700 : 500 }}>{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ─── TOPBAR ──────────────────────────────────────────────────────────────────
function Topbar({ platform, setPlatform, onLogout, onMenuOpen }) {
  const [showLogoutDropdown, setShowLogoutDropdown] = useState(false);
  const platforms = [
    { key: "whatsapp",  icon: "ti-brand-whatsapp", color: "#25D366", bg: "#0d2b17" },
    { key: "facebook",  icon: "ti-brand-facebook",  color: "#1877F2", bg: "#0d1a2b" },
    { key: "instagram", icon: "ti-brand-instagram",  color: "#E1306C", bg: "#2b0d1a" },
  ];
  return (
    <header className="flex items-center gap-2 lg:gap-4 px-3 lg:px-6 py-3 border-b flex-shrink-0"
      style={{ background: "#161412", borderColor: "#2a2825" }}>
      {/* Hamburger — mobile only */}
      <button onClick={onMenuOpen}
        className="lg:hidden w-8 h-8 rounded-xl flex items-center justify-center text-stone-400 flex-shrink-0"
        style={{ background: "#242220" }}>
        <i className="ti ti-menu-2 text-base" />
      </button>

      {/* Theme toggles — desktop only */}
      <div className="hidden lg:flex gap-1.5">
        {["ti-moon-stars","ti-sun"].map((ic) => (
          <button key={ic} className="w-7 h-7 rounded-full flex items-center justify-center text-stone-500 hover:text-stone-300"
            style={{ background: "#242220" }}>
            <i className={`ti ${ic} text-xs`} />
          </button>
        ))}
      </div>

      <div className="flex-1 flex items-center gap-2 rounded-full px-3 lg:px-4 py-2 border lg:mx-4"
        style={{ background: "#1e1c1a", borderColor: "#333" }}>
        <i className="ti ti-search text-stone-600 text-xs" />
        <span className="text-[11px] text-stone-600 hidden sm:block">Search conversations…</span>
        <span className="text-[11px] text-stone-600 sm:hidden">Search…</span>
      </div>

      {/* Platform switcher circles */}
      <div className="flex items-center gap-1.5 lg:gap-2 flex-shrink-0">
        {platforms.map(({ key, icon, color, bg }) => (
          <button key={key} onClick={() => setPlatform(key)} title={key}
            className="w-7 h-7 lg:w-8 lg:h-8 rounded-full flex items-center justify-center transition-all duration-200 flex-shrink-0"
            style={{
              background: platform === key ? bg : "#242220",
              border: platform === key ? `2px solid ${color}` : "2px solid transparent",
              boxShadow: platform === key ? `0 0 10px ${color}50` : "none",
            }}>
            <i className={`ti ${icon} text-xs lg:text-sm`} style={{ color: platform === key ? color : "#666" }} />
          </button>
        ))}

        {/* Logout button with dropdown */}
        <div className="relative ml-1">
          <button
            onMouseEnter={() => setShowLogoutDropdown(true)}
            onMouseLeave={() => setShowLogoutDropdown(false)}
            onClick={onLogout}
            className="w-8 h-8 lg:w-9 lg:h-9 rounded-xl flex items-center justify-center text-white hover:bg-orange-700 transition-all duration-200 shadow-lg"
            style={{ background: "#e8602a" }}
            title="Logout">
            <i className="ti ti-power text-base lg:text-lg" />
          </button>
          {showLogoutDropdown && (
            <div onMouseEnter={() => setShowLogoutDropdown(true)} onMouseLeave={() => setShowLogoutDropdown(false)}
              className="absolute right-0 top-full mt-1 w-32 rounded-xl border shadow-2xl py-1 z-50"
              style={{ background: "#1a1815", borderColor: "#2a2825" }}>
              <button onClick={onLogout}
                className="w-full px-4 py-2.5 text-left text-sm text-stone-300 hover:bg-orange-500/10 hover:text-orange-400 transition-colors flex items-center gap-2">
                <i className="ti ti-logout text-orange-500" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ─── DATA ────────────────────────────────────────────────────────────────────
const allConversations = {
  whatsapp: [
    { id: 1, name: "Sarah Mitchell",  avatar: "SM", color: "bg-orange-700",
      preview: "Hey! I wanted to follow up on the proposal…", time: "2m ago",  unread: 3, status: "online", tag: "Lead",     tagColor: "bg-orange-600/20 text-orange-400" },
    { id: 2, name: "Carlos Rivera",   avatar: "CR", color: "bg-rose-800",
      preview: "When will the new features be available?",   time: "3h ago",  unread: 0, status: "offline", tag: "Lead",     tagColor: "bg-orange-600/20 text-orange-400" },
    { id: 3, name: "Lena Hoffman",    avatar: "LH", color: "bg-indigo-800",
      preview: "Perfect, see you at 3pm! Don't forget.",     time: "Yesterday",unread: 0, status: "away",  tag: "Customer", tagColor: "bg-emerald-600/20 text-emerald-400" },
    { id: 4, name: "Bilal Chaudhry",  avatar: "BC", color: "bg-green-800",
      preview: "Can you confirm the meeting for tomorrow?",  time: "Yesterday",unread: 1, status: "online", tag: "Lead",     tagColor: "bg-orange-600/20 text-orange-400" },
    { id: 5, name: "Nadia Farooq",    avatar: "NF", color: "bg-teal-800",
      preview: "The payment has been processed, thanks!",    time: "Mon",     unread: 0, status: "offline", tag: "Customer", tagColor: "bg-emerald-600/20 text-emerald-400" },
  ],
  facebook: [
    { id: 1, name: "James Thornton",  avatar: "JT", color: "bg-blue-800",
      preview: "Can you send me the updated pricing?",       time: "18m ago", unread: 1, status: "online",  tag: "Customer", tagColor: "bg-emerald-600/20 text-emerald-400" },
    { id: 2, name: "Aisha Patel",     avatar: "AP", color: "bg-purple-800",
      preview: "The integration is working perfectly now!",  time: "1h ago",  unread: 0, status: "away",    tag: "Customer", tagColor: "bg-emerald-600/20 text-emerald-400" },
    { id: 3, name: "Marcus Webb",     avatar: "MW", color: "bg-blue-700",
      preview: "Loved the demo! When can we get started?",   time: "4h ago",  unread: 2, status: "online",  tag: "Lead",     tagColor: "bg-orange-600/20 text-orange-400" },
    { id: 4, name: "Sophie Laurent",  avatar: "SL", color: "bg-cyan-800",
      preview: "Do you have a free trial available?",        time: "Yesterday",unread: 0, status: "offline", tag: "Lead",     tagColor: "bg-orange-600/20 text-orange-400" },
  ],
  instagram: [
    { id: 1, name: "Emma Schultz",    avatar: "ES", color: "bg-pink-800",
      preview: "Saw your post — love what you're doing! 🔥", time: "5m ago",  unread: 4, status: "online",  tag: "Partner",  tagColor: "bg-sky-600/20 text-sky-400" },
    { id: 2, name: "David Okonkwo",   avatar: "DO", color: "bg-amber-800",
      preview: "Could we reschedule tomorrow's demo?",       time: "2h ago",  unread: 0, status: "offline", tag: "Lead",     tagColor: "bg-orange-600/20 text-orange-400" },
    { id: 3, name: "Zara Ahmed",      avatar: "ZA", color: "bg-rose-700",
      preview: "Your product looks amazing, DM'd you!",      time: "6h ago",  unread: 1, status: "online",  tag: "Lead",     tagColor: "bg-orange-600/20 text-orange-400" },
    { id: 4, name: "Kenji Tanaka",    avatar: "KT", color: "bg-violet-800",
      preview: "Reposted your story — great content!",       time: "Yesterday",unread: 0, status: "away",   tag: "Customer", tagColor: "bg-emerald-600/20 text-emerald-400" },
  ],
};

const allMessages = {
  whatsapp: {
    1: [
      { id:1, from:"them", avatar:"SM", color:"bg-orange-700", text:"Hey! I wanted to follow up on the proposal you sent last week. We've reviewed it internally.", time:"10:24 AM" },
      { id:2, from:"them", avatar:"SM", color:"bg-orange-700", text:"The team really liked the AI features, especially the automated lead scoring. Could you walk us through onboarding?", time:"10:25 AM" },
      { id:3, from:"me",   text:"Hi Sarah! Great to hear the team liked it. I'd be happy to walk you through the full onboarding.", time:"10:31 AM" },
      { id:4, from:"me",   text:"We typically start with a kickoff call, then a guided setup session. Your team will be up and running same day.", time:"10:32 AM" },
      { id:5, from:"them", avatar:"SM", color:"bg-orange-700", text:"That sounds very smooth. What about data migration from our current CRM?", time:"10:45 AM" },
      { id:6, from:"ai",   text:"AI Suggestion: Sarah is asking about CRM data migration. Mention our built-in CSV import and integrations with Salesforce, HubSpot, and Pipedrive." },
      { id:7, from:"them", avatar:"SM", color:"bg-orange-700", text:"Also, do you offer a trial period before we commit to an annual plan?", time:"10:47 AM" },
    ],
  },
  facebook: {
    1: [
      { id:1, from:"them", avatar:"JT", color:"bg-blue-800", text:"Hi! I saw your Facebook ad and I'm really interested in the enterprise plan. Can you send me updated pricing?", time:"9:10 AM" },
      { id:2, from:"me",   text:"Hi James! Thanks for reaching out. I'll send you our enterprise pricing deck right away.", time:"9:18 AM" },
      { id:3, from:"them", avatar:"JT", color:"bg-blue-800", text:"Perfect. Also, does it include custom integrations?", time:"9:20 AM" },
      { id:4, from:"ai",   text:"AI Suggestion: James is asking about custom integrations. Highlight our API access, Zapier support, and dedicated integration onboarding." },
      { id:5, from:"me",   text:"Absolutely — all enterprise plans include API access and our team handles custom integration setup for you.", time:"9:25 AM" },
    ],
  },
  instagram: {
    1: [
      { id:1, from:"them", avatar:"ES", color:"bg-pink-800", text:"Hey! Saw your reel about the AI dashboard — it looks incredible! 🔥 Is this available for small businesses too?", time:"8:05 AM" },
      { id:2, from:"me",   text:"Hey Emma! Yes, absolutely! We have plans starting from small teams all the way to enterprise.", time:"8:12 AM" },
      { id:3, from:"them", avatar:"ES", color:"bg-pink-800", text:"Amazing! Do you have a link I can check out? Also love the UI design btw 😍", time:"8:14 AM" },
      { id:4, from:"ai",   text:"AI Suggestion: Emma is interested and complimenting the UI. Great moment to share a demo link and offer a free trial." },
      { id:5, from:"me",   text:"Thank you so much! Here's a link to our 14-day free trial — no credit card needed: nigella.ai/trial 🚀", time:"8:20 AM" },
      { id:6, from:"them", avatar:"ES", color:"bg-pink-800", text:"Signing up right now! Can't wait to try it 🙌", time:"8:22 AM" },
    ],
  },
};

const platformConfig = {
  whatsapp:  { label: "WhatsApp",  icon: "ti-brand-whatsapp",  color: "#25D366", bg: "#0d2b17", badge: "WA" },
  facebook:  { label: "Facebook",  icon: "ti-brand-facebook",   color: "#1877F2", bg: "#0d1a2b", badge: "FB" },
  instagram: { label: "Instagram", icon: "ti-brand-instagram",  color: "#E1306C", bg: "#2b0d1a", badge: "IG" },
};

const statusDot = (s) =>
  s === "online" ? "bg-emerald-400" : s === "away" ? "bg-amber-400" : "bg-stone-600";

// ─── CONV LIST ────────────────────────────────────────────────────────────────
function ConvList({ platform, selected, onSelect }) {
  const [filter, setFilter] = useState("All");
  const pc = platformConfig[platform];
  const convs = allConversations[platform] || [];
  const filters = ["All","Open","Resolved","Unread"];

  return (
    <div className="flex flex-col h-full w-full lg:border-r flex-shrink-0"
      style={{ lg: {}, width: undefined, minWidth: undefined, background: "#161412", borderColor: "#2a2825" }}>
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b" style={{ borderColor: "#2a2825" }}>
        {/* Platform badge */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-xl flex items-center justify-center"
              style={{ background: pc.bg, border: `1px solid ${pc.color}40` }}>
              <i className={`ti ${pc.icon} text-sm`} style={{ color: pc.color }} />
            </div>
            <div>
              <div className="text-sm font-bold text-stone-100 leading-tight">Conversations</div>
              <div className="text-[9px] font-medium" style={{ color: pc.color }}>{pc.label}</div>
            </div>
          </div>
          <div className="flex gap-1.5">
            <button className="w-7 h-7 rounded-lg flex items-center justify-center text-stone-500 hover:text-orange-400"
              style={{ background: "#242220" }}>
              <i className="ti ti-filter text-xs" />
            </button>
            <button className="w-7 h-7 rounded-lg flex items-center justify-center text-white"
              style={{ background: "#e8602a" }}>
              <i className="ti ti-plus text-xs" />
            </button>
          </div>
        </div>
        {/* Search */}
        <div className="flex items-center gap-2 rounded-xl px-3 py-2 border"
          style={{ background: "#1e1c1a", borderColor: "#2a2825" }}>
          <i className="ti ti-search text-stone-600 text-xs" />
          <input className="bg-transparent text-[11px] text-stone-400 placeholder-stone-700 outline-none w-full"
            placeholder={`Search ${pc.label}…`} />
        </div>
        {/* Filters */}
        <div className="flex gap-1 mt-3 overflow-x-auto">
          {filters.map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-[10px] rounded-lg px-2.5 py-1 font-medium transition-all flex-shrink-0 ${
                filter === f ? "text-white" : "text-stone-500 hover:text-stone-300"}`}
              style={filter === f ? { background: pc.color } : { background: "#252320" }}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto py-1">
        {convs.map((c) => (
          <div key={c.id} onClick={() => onSelect(c.id)}
            className={`px-4 py-3 cursor-pointer transition-all border-l-2 ${
              selected === c.id ? "bg-orange-600/10 border-orange-500" : "border-transparent hover:bg-white/[0.03]"}`}>
            <div className="flex items-start gap-3">
              <div className="relative flex-shrink-0">
                <div className={`w-10 h-10 lg:w-9 lg:h-9 rounded-full ${c.color} flex items-center justify-center text-white font-semibold`}
                  style={{ fontSize: 11 }}>{c.avatar}</div>
                <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 ${statusDot(c.status)}`}
                  style={{ borderColor: "#161412" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-semibold text-stone-100 truncate">{c.name}</span>
                  <span className="text-[9px] text-stone-600 flex-shrink-0">{c.time}</span>
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <i className={`ti ${pc.icon} text-[9px]`} style={{ color: pc.color }} />
                  <span className="text-[10px] text-stone-500 truncate">{c.preview}</span>
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className={`text-[9px] rounded-full px-2 py-0.5 font-medium ${c.tagColor}`}>{c.tag}</span>
                  {c.unread > 0 && (
                    <span className="w-4 h-4 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ background: pc.color, fontSize: 8 }}>{c.unread}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CHAT WINDOW ─────────────────────────────────────────────────────────────
function ChatWindow({ platform, convId, onBack, onInfo }) {
  const [input, setInput] = useState("");
  const pc = platformConfig[platform];
  const convs = allConversations[platform] || [];
  const conv = convs.find((c) => c.id === convId) || convs[0];
  const msgs = (allMessages[platform]?.[convId]) || (allMessages[platform]?.[1]) || [];

  if (!conv) return null;

  return (
    <div className="flex-1 flex flex-col min-w-0 h-full w-full" style={{ background: "#111010" }}>
      {/* Header */}
      <div className="flex items-center gap-2 lg:gap-3 px-3 lg:px-5 py-3 lg:py-3.5 border-b flex-shrink-0"
        style={{ background: "#161412", borderColor: "#2a2825" }}>
        {/* Back button — mobile only */}
        <button onClick={onBack}
          className="lg:hidden w-8 h-8 rounded-xl flex items-center justify-center text-stone-400 flex-shrink-0"
          style={{ background: "#242220" }}>
          <i className="ti ti-chevron-left text-base" />
        </button>

        <div className="relative flex-shrink-0">
          <div className={`w-9 h-9 rounded-full ${conv.color} flex items-center justify-center text-white font-semibold`}
            style={{ fontSize: 11 }}>{conv.avatar}</div>
          <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 ${statusDot(conv.status)}`}
            style={{ borderColor: "#161412" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-stone-100 truncate">{conv.name}</div>
          <div className="flex items-center gap-1.5 text-[10px] text-stone-500">
            <i className={`ti ${pc.icon} text-xs`} style={{ color: pc.color }} />
            <span style={{ color: pc.color }}>{pc.label}</span>
            <span className="hidden sm:inline">·</span>
            <span className="capitalize hidden sm:inline">{conv.status}</span>
          </div>
        </div>

        {/* Desktop actions */}
        <div className="hidden lg:flex items-center gap-1.5">
          {["ti-phone","ti-video"].map((ic) => (
            <button key={ic}
              className="w-8 h-8 rounded-xl flex items-center justify-center text-stone-500 hover:text-stone-300"
              style={{ background: "#242220" }}>
              <i className={`ti ${ic} text-sm`} />
            </button>
          ))}
          <button onClick={onInfo}
            className="w-8 h-8 rounded-xl flex items-center justify-center text-stone-500 hover:text-stone-300"
            style={{ background: "#242220" }}>
            <i className="ti ti-dots-vertical text-sm" />
          </button>
        </div>

        {/* Mobile info button — opens contact slide-over */}
        <button onClick={onInfo}
          className="lg:hidden w-8 h-8 rounded-xl flex items-center justify-center text-stone-400 flex-shrink-0"
          style={{ background: "#242220" }}>
          <i className="ti ti-info-circle text-base" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 lg:px-5 py-4 flex flex-col gap-3">
        {/* Date divider */}
        <div className="flex items-center gap-3 my-1">
          <div className="flex-1 h-px" style={{ background: "#2a2825" }} />
          <span className="text-[10px] text-stone-600 px-2">Today</span>
          <div className="flex-1 h-px" style={{ background: "#2a2825" }} />
        </div>

        {msgs.map((m) => {
          if (m.from === "ai") return (
            <div key={m.id} className="flex items-start gap-2 rounded-xl px-3 py-2.5 border"
              style={{ background: "#1a1f15", borderColor: "#2a3a20" }}>
              <div className="w-6 h-6 rounded-lg bg-orange-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ti ti-robot text-white" style={{ fontSize: 10 }} />
              </div>
              <div>
                <div className="text-[9px] text-orange-400 font-semibold mb-0.5 uppercase tracking-wide">AI Suggestion</div>
                <div className="text-[11px] text-stone-300 leading-relaxed">{m.text}</div>
              </div>
            </div>
          );
          if (m.from === "me") return (
            <div key={m.id} className="flex justify-end">
              <div className="max-w-[85%] sm:max-w-[70%] lg:max-w-[62%]">
                <div className="rounded-2xl rounded-tr-sm px-4 py-2.5 text-[11px] text-white leading-relaxed"
                  style={{ background: "#e8602a" }}>{m.text}</div>
                <div className="text-[9px] text-stone-600 text-right mt-1 flex items-center justify-end gap-1">
                  {m.time}
                  <i className="ti ti-checks text-orange-400" style={{ fontSize: 10 }} />
                </div>
              </div>
            </div>
          );
          return (
            <div key={m.id} className="flex items-end gap-2">
              <div className={`w-7 h-7 rounded-full ${m.color} flex items-center justify-center text-white flex-shrink-0 mb-1`}
                style={{ fontSize: 9 }}>{m.avatar}</div>
              <div className="max-w-[85%] sm:max-w-[70%] lg:max-w-[62%]">
                <div className="rounded-2xl rounded-tl-sm px-4 py-2.5 text-[11px] text-stone-200 leading-relaxed"
                  style={{ background: "#1d1b19", border: "1px solid #2a2825" }}>{m.text}</div>
                <div className="text-[9px] text-stone-600 mt-1">{m.time}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="px-3 lg:px-5 py-3 border-t flex-shrink-0" style={{ borderColor: "#2a2825", background: "#161412" }}>
        <div className="hidden sm:flex items-center gap-2 mb-2">
          {["ti-paperclip","ti-mood-smile","ti-photo","ti-template"].map((ic) => (
            <button key={ic}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-stone-500 hover:text-orange-400"
              style={{ background: "#252320" }}>
              <i className={`ti ${ic} text-sm`} />
            </button>
          ))}
          <div className="flex-1" />
          <span className="text-[9px] text-stone-700 mr-1">AI assist is ON</span>
          <div className="w-6 h-3.5 rounded-full bg-orange-600 flex items-center justify-end pr-0.5 cursor-pointer">
            <div className="w-2.5 h-2.5 rounded-full bg-white" />
          </div>
        </div>
        <div className="flex items-center gap-2 lg:gap-3 rounded-2xl px-3 lg:px-4 py-2.5 border"
          style={{ background: "#1e1c1a", borderColor: "#333" }}>
          {/* Platform badge in input — desktop */}
          <div className="hidden sm:flex items-center gap-1.5 px-2 py-0.5 rounded-lg flex-shrink-0"
            style={{ background: pc.bg, border: `1px solid ${pc.color}30` }}>
            <i className={`ti ${pc.icon} text-xs`} style={{ color: pc.color }} />
            <span className="text-[9px] font-semibold" style={{ color: pc.color }}>{pc.badge}</span>
          </div>
          <button className="sm:hidden flex-shrink-0 text-stone-500">
            <i className="ti ti-paperclip text-sm" />
          </button>
          <input
            className="flex-1 min-w-0 bg-transparent text-[12px] text-stone-300 placeholder-stone-700 outline-none"
            placeholder={`Reply via ${pc.label}…`}
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <button className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-all"
            style={{ background: input.trim() ? "#e8602a" : "#2a2825" }}>
            <i className="ti ti-send text-white text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CONTACT PANEL ───────────────────────────────────────────────────────────
function ContactPanel({ platform, convId, mobileOpen, onClose }) {
  const [tab, setTab] = useState("details");
  const pc = platformConfig[platform];
  const convs = allConversations[platform] || [];
  const conv = convs.find((c) => c.id === convId) || convs[0];
  if (!conv) return null;

  const tabs = ["details","history","ai"];

  const Content = (
    <>
      {/* Profile */}
      <div className="flex flex-col items-center px-5 py-5 border-b text-center" style={{ borderColor: "#2a2825" }}>
        <div className="relative mb-3">
          <div className={`w-14 h-14 rounded-full ${conv.color} flex items-center justify-center text-white font-bold text-lg`}>
            {conv.avatar}
          </div>
          <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ background: pc.bg, border: `2px solid ${pc.color}` }}>
            <i className={`ti ${pc.icon} text-[10px]`} style={{ color: pc.color }} />
          </div>
        </div>
        <div className="text-sm font-bold text-stone-100">{conv.name}</div>
        <div className="flex items-center gap-1.5 mt-1">
          <div className={`w-2 h-2 rounded-full ${statusDot(conv.status)}`} />
          <span className="text-[10px] text-stone-500 capitalize">{conv.status}</span>
        </div>
        <span className={`mt-2 text-[10px] rounded-full px-3 py-0.5 font-medium ${conv.tagColor}`}>{conv.tag}</span>
        <div className="flex items-center gap-1.5 mt-2 rounded-lg px-3 py-1"
          style={{ background: pc.bg, border: `1px solid ${pc.color}30` }}>
          <i className={`ti ${pc.icon} text-xs`} style={{ color: pc.color }} />
          <span className="text-[10px] font-semibold" style={{ color: pc.color }}>via {pc.label}</span>
        </div>
        <div className="flex gap-2 mt-4">
          {[{ ic:"ti-phone", bg:"#252320" },{ ic:"ti-mail", bg:"#252320" },{ ic: pc.icon, bg: pc.bg }].map(({ ic, bg }, idx) => (
            <button key={idx}
              className="w-8 h-8 rounded-xl flex items-center justify-center text-stone-400 hover:text-stone-200"
              style={{ background: bg }}>
              <i className={`ti ${ic} text-sm`} style={idx === 2 ? { color: pc.color } : {}} />
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: "#2a2825" }}>
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 py-2 text-[9px] font-semibold uppercase tracking-wide transition-colors ${
              tab === t ? "text-orange-400 border-b-2 border-orange-500" : "text-stone-600 hover:text-stone-400"}`}
            style={{ marginBottom: tab === t ? -1 : 0 }}>
            {t === "details" ? "Contact" : t === "history" ? "History" : "AI"}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {tab === "details" && (
          <div className="flex flex-col gap-3">
            {[
              { ic:"ti-mail",     label:"Email",    val:"sarah@company.co" },
              { ic:"ti-phone",    label:"Phone",    val:"+1 (555) 203-4411" },
              { ic:"ti-map-pin",  label:"Location", val:"San Francisco, CA" },
              { ic:"ti-building", label:"Company",  val:"Mitchell & Co." },
            ].map(({ ic, label, val }) => (
              <div key={label} className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: "#252320" }}>
                  <i className={`ti ${ic} text-orange-500`} style={{ fontSize: 11 }} />
                </div>
                <div>
                  <div className="text-[9px] text-stone-600 uppercase tracking-wide">{label}</div>
                  <div className="text-[11px] text-stone-300 mt-0.5">{val}</div>
                </div>
              </div>
            ))}
            <div>
              <div className="text-[9px] text-stone-600 uppercase tracking-wide mb-2">Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {["Enterprise","Hot Lead","Demo Done"].map((t) => (
                  <span key={t} className="text-[9px] text-stone-400 rounded-full px-2 py-0.5"
                    style={{ background: "#252320" }}>{t}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[9px] text-stone-600 uppercase tracking-wide mb-2">Notes</div>
              <textarea className="w-full rounded-xl p-2.5 text-[10px] text-stone-400 placeholder-stone-700 outline-none resize-none border"
                style={{ background: "#1e1c1a", borderColor: "#2a2825", height: 64 }}
                placeholder="Add a note…" />
            </div>
          </div>
        )}
        {tab === "history" && (
          <div className="flex flex-col gap-2.5">
            {[
              { label:"Proposal Sent",   time:"Jun 20", ic:"ti-file-text",    color:"text-orange-400" },
              { label:"Demo Completed",  time:"Jun 15", ic:"ti-video",        color:"text-blue-400" },
              { label:"First Contact",   time:"Jun 10", ic:"ti-message-dots", color:"text-purple-400" },
            ].map(({ label, time, ic, color }) => (
              <div key={label} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5"
                style={{ background: "#1d1b19", border: "1px solid #2a2825" }}>
                <i className={`ti ${ic} ${color} text-base`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-stone-200 font-medium">{label}</div>
                  <div className="text-[9px] text-stone-600">{time}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {tab === "ai" && (
          <div className="flex flex-col gap-3">
            <div className="rounded-xl p-3 border" style={{ background: "#1a1f15", borderColor: "#2a3a20" }}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <i className="ti ti-brain text-orange-400" style={{ fontSize: 11 }} />
                <span className="text-[9px] text-orange-400 font-semibold uppercase tracking-wide">Sentiment</span>
              </div>
              <div className="text-[11px] text-stone-300">Positive — customer is engaged and interested in closing.</div>
            </div>
            <div className="rounded-xl p-3 border" style={{ background: "#1d1b19", borderColor: "#2a2825" }}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <i className="ti ti-target text-blue-400" style={{ fontSize: 11 }} />
                <span className="text-[9px] text-blue-400 font-semibold uppercase tracking-wide">Deal Score</span>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-stone-100">87</span>
                <span className="text-xs text-stone-500">/ 100</span>
              </div>
              <div className="h-1.5 rounded-full mt-2" style={{ background: "#252320" }}>
                <div className="h-full rounded-full" style={{ width:"87%", background:"linear-gradient(90deg,#c84a1e,#e8602a)" }} />
              </div>
            </div>
            <div className="rounded-xl p-3 border" style={{ background: "#1d1b19", borderColor: "#2a2825" }}>
              <div className="text-[9px] text-stone-500 uppercase tracking-wide mb-2">Suggested Actions</div>
              {["Send pricing PDF","Schedule follow-up call","Share case study"].map((a) => (
                <div key={a} className="flex items-center gap-2 py-1.5 border-b last:border-0" style={{ borderColor: "#2a2825" }}>
                  <i className="ti ti-circle-check text-orange-500" style={{ fontSize: 11 }} />
                  <span className="text-[10px] text-stone-300">{a}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-4 flex flex-col gap-2 border-t pt-3" style={{ borderColor: "#2a2825" }}>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-stone-500">Assigned to</span>
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-orange-700 flex items-center justify-center text-white" style={{ fontSize: 7 }}>Y</div>
            <span className="text-[10px] text-stone-300">You</span>
          </div>
        </div>
        <button className="w-full rounded-xl py-2 text-xs font-semibold text-white hover:opacity-90 transition-all"
          style={{ background: "#e8602a" }}>
          Mark as Resolved
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop — always-visible right rail */}
      <div className="hidden lg:flex flex-col h-full border-l flex-shrink-0"
        style={{ width: 240, background: "#161412", borderColor: "#2a2825" }}>
        {Content}
      </div>

      {/* Mobile — slide-over drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.65)" }} onClick={onClose} />
          <div className="relative flex flex-col h-full w-[82%] max-w-[300px] animate-[slideIn_0.25s_ease-out]"
            style={{ background: "#161412" }}>
            <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0" style={{ borderColor: "#2a2825" }}>
              <span className="text-xs font-bold text-stone-200 uppercase tracking-wide">Contact Info</span>
              <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-stone-400"
                style={{ background: "#242220" }}>
                <i className="ti ti-x text-sm" />
              </button>
            </div>
            <div className="flex-1 flex flex-col overflow-y-auto">
              {Content}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function Conversations({ onLogout }) {
  const [platform, setPlatform] = useState("whatsapp");
  const [selected, setSelected]  = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  // mobile-only view state: which pane is showing on small screens
  const [mobileScreen, setMobileScreen] = useState("list"); // "list" | "chat"

  const handlePlatformChange = (p) => {
    setPlatform(p);
    setSelected(1);
    setMobileScreen("list");
  };

  const handleSelectConv = (id) => {
    setSelected(id);
    setMobileScreen("chat");
    setContactOpen(false);
  };

  const handleBackToList = () => {
    setMobileScreen("list");
    setContactOpen(false);
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden" style={{ background: "#111010" }}>
      <Sidebar />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Topbar platform={platform} setPlatform={handlePlatformChange} onLogout={onLogout} onMenuOpen={() => setDrawerOpen(true)} />

        <div className="flex flex-1 min-h-0 overflow-hidden relative">
          {/* Conversation list — full width on mobile when in "list" mode, fixed column on desktop */}
          <div className={`${mobileScreen === "chat" ? "hidden" : "flex"} lg:flex h-full flex-shrink-0`}
            style={{ width: undefined }}>
            <div className="flex h-full w-full lg:w-[280px] lg:min-w-[240px] border-r" style={{ borderColor: "#2a2825" }}>
              <ConvList platform={platform} selected={selected} onSelect={handleSelectConv} />
            </div>
          </div>

          {/* Chat window — full width on mobile when in "chat" mode, flex-1 on desktop */}
          <div className={`${mobileScreen === "list" ? "hidden" : "flex"} lg:flex flex-1 h-full min-w-0`}>
            <ChatWindow
              platform={platform}
              convId={selected}
              onBack={handleBackToList}
              onInfo={() => setContactOpen(true)}
            />
          </div>

          <ContactPanel
            platform={platform}
            convId={selected}
            mobileOpen={contactOpen}
            onClose={() => setContactOpen(false)}
          />
        </div>
      </div>

      {/* Mobile bottom nav — hidden while a chat is open for a full-screen feel */}
      {mobileScreen !== "chat" && <MobileBottomNav />}
    </div>
  );
}